/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { courseraPlusProductVariants } from 'bundles/coursera-plus-common/constants/CourseraPlusProductVariant';
import { SUBSCRIBE_TO_COURSERA_PLUS } from 'bundles/enroll-common/common/EnrollmentChoiceTypes';
import {
  choiceTypeToHandleSubmitPromise,
  submitEnrollmentPromise,
} from 'bundles/enroll-common/lib/enrollmentChoiceUtils';
import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { getPrePurchaseUpsellCopies } from 'bundles/enroll-modal/components/coursera-plus/prePurchaseUpsell/CourseraPlusPrePurchaseUpsellCopies';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import type { ProductPrices } from 'bundles/ownable-products-common/hooks/useGetProductPrices';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import CourseraPlusLogo from 'images/coursera-plus/prePurchaseUpsell/cplus_logo.svg';
import LadyWithTablet from 'images/coursera-plus/prePurchaseUpsell/lady_with_tablet.png';
import PartnerLogos from 'images/coursera-plus/prePurchaseUpsell/partner_logos.svg';

import _t from 'i18n!nls/enroll-course';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type CourseraPlusPrePurchaseUpsellModalProps = {
  onDeclinePrePurchaseUpsell: (shouldGoToCheckoutOnClose: boolean) => void;
  onError: (apiError: ApiError) => void;
  courseId: string;
  s12nId?: string;
  isS12n?: boolean;
};

const styles = {
  dialog: css`
    .cds-Dialog-dialog {
      background-color: var(--cds-color-interactive-primary);
      max-width: 800px;
    }
  `,

  heading: css`
    padding-bottom: var(--cds-spacing-200);
  `,

  buttonsContainer: css`
    display: flex;
    gap: var(--cds-spacing-300);
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-200);
    align-items: start;

    ${breakpoints.down('sm')} {
      flex-direction: column;
      margin: var(--cds-spacing-400) 0;
    }
  `,

  priceBadge: css`
    position: absolute;
    top: 3%;
    right: 5%;
    min-width: 127px;
    border-radius: 0 8px 8px 8px;
    background: linear-gradient(
      283deg,
      var(--cds-color-pink-300, #ff82e7) 17.78%,
      var(--cds-color-pink-600, #c429a8) 101.74%
    );
    color: white;
    padding: 12px;
    text-align: center;

    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      width: 85%;
      height: 100%;
      border-radius: 8px;
      background-color: var(--cds-color-blue-950, #002761);
      transform: rotate(-3deg);
      z-index: -5;
    }
  `,

  girlLookingAtTabletImage: css`
    z-index: -10;
    margin-top: -44px;
    position: relative;
  `,

  partnerLogosImage: css`
    position: absolute;
    bottom: 12%;
    right: 40%;
  `,

  cplusLogo: css`
    position: absolute;
    right: 18%;
    top: -5.4%;
  `,

  heroSection: css`
    position: relative;
    margin-bottom: var(--cds-spacing-200);
    max-width: 331px;

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-150) 0;
    }
  `,

  heroSectionContainer: css`
    display: flex;
    justify-content: center;
  `,

  pricePillAmountText: css`
    font-size: 28px;
    line-height: 32px;
    letter-spacing: -0.14px;
  `,

  desktopContentContainer: css`
    margin: 0;
    margin-top: -16px;
  `,
};

export const CourseraPlusPrePurchaseUpsellModal = ({
  onDeclinePrePurchaseUpsell,
  onError,
  courseId,
  s12nId,
  isS12n = false, // s12n and course-in-s12n
}: CourseraPlusPrePurchaseUpsellModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeclineLoading, setIsDeclineLoading] = useState(false);
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const {
    courseraPlusMonthlyUpsellProductPrice,
    productPrice: courseProductPrice,
    s12nDerivatives,
    s12nSubscriptionOwnableProduct,
  } = usePageData();
  const { trackEnrollModalContinue } = useEnrollModalEventing({ isUsingPrePurchaseUpsellFlow: true });

  const s12nProductPrice = s12nSubscriptionOwnableProduct?.price || s12nDerivatives?.catalogPrice;
  const productPrice = (isS12n ? s12nProductPrice : courseProductPrice) as ProductPrices;
  if (!courseraPlusMonthlyUpsellProductPrice || !productPrice) {
    return null;
  }
  const { heading, description, courseraPlusMonthlyPriceDisplay } = getPrePurchaseUpsellCopies({
    isStandaloneCourse: !isS12n,
    productPrice,
    courseraPlusMonthlyPrice: courseraPlusMonthlyUpsellProductPrice,
  });

  const handleContinue = () => {
    setIsLoading(true);

    trackEnrollModalContinue(SUBSCRIBE_TO_COURSERA_PLUS, {
      productItemId: courseraPlusProductVariants.MONTHLY_UPSELL_UPGRADE,
    });

    const handleSubmitPromise = choiceTypeToHandleSubmitPromise[SUBSCRIBE_TO_COURSERA_PLUS];
    const options = {
      productItemId: courseraPlusProductVariants.MONTHLY_UPSELL_UPGRADE,
      courseId,
      s12nId,
      acquisitionType: 'PRE_PURCHASE_UPSELL' as const,
    };
    submitEnrollmentPromise({ handleSubmitPromise, options }).catch(onError);
  };

  const handleDecline = () => {
    setIsDeclineLoading(true);
    onDeclinePrePurchaseUpsell(true);
  };

  const renderHeading = (headingText: string | React.JSX.Element) => {
    return (
      <Typography2 variant="titleLargeCompact" component="h1" color="invertBody" css={styles.heading}>
        {headingText}
      </Typography2>
    );
  };

  const renderDescription = (descriptionText: string | React.JSX.Element) => {
    return (
      <Typography2 variant="bodySecondary" component="div" color="invertBody">
        {descriptionText}
      </Typography2>
    );
  };

  const renderPricePill = () => {
    const priceDisplay = (
      <Typography2 variant="subtitleLarge" component="span" color="invertBody" css={styles.pricePillAmountText}>
        {courseraPlusMonthlyPriceDisplay}
      </Typography2>
    );
    const perMonthDisplay = (
      <Typography2 variant="bodyPrimary" component="span" color="invertBody">
        {_t('/month')}
      </Typography2>
    );
    return (
      <div css={styles.priceBadge}>
        <FormattedMessage message={_t('{price}{perMonth}')} price={priceDisplay} perMonth={perMonthDisplay} />
      </div>
    );
  };

  const renderHeroSection = () => {
    return (
      <div css={styles.heroSection}>
        <Imgix src={CourseraPlusLogo} alt={_t('Coursera Plus Logo')} height={24} css={styles.cplusLogo} />
        {renderPricePill()}
        <Imgix src={LadyWithTablet} height={331} alt={_t('Lady with tablet')} css={styles.girlLookingAtTabletImage} />
        <Imgix src={PartnerLogos} alt={_t('Partner Logos')} height={32} css={styles.partnerLogosImage} />
      </div>
    );
  };

  const renderActions = () => {
    return (
      <div css={styles.buttonsContainer}>
        <TrackedButton
          variant="primaryInvert"
          onClick={handleContinue}
          loading={isLoading}
          disabled={isLoading}
          trackingName="pre_purchase_upsell_continue_button"
          data-testid="pre_purchase_upsell_continue_button"
          size={isMobile ? 'medium' : 'small'}
          fullWidth={isMobile}
        >
          {_t('Upgrade now')}
        </TrackedButton>
        <TrackedButton
          onClick={handleDecline}
          loading={isDeclineLoading}
          disabled={isLoading || isDeclineLoading}
          variant="ghostInvert"
          trackingName="pre_purchase_upsell_decline_button"
          data-testid="pre_purchase_upsell_decline_button"
          size={isMobile ? 'medium' : 'small'}
          fullWidth={isMobile}
        >
          {_t('Decline offer')}
        </TrackedButton>
      </div>
    );
  };

  const renderDesktopContent = () => {
    return (
      <Grid container spacing={24} css={styles.desktopContentContainer}>
        <Grid item sm={12} md={6}>
          {renderHeading(heading)}
          {renderDescription(description)}
          {renderActions()}
        </Grid>
        <Grid item sm={12} md={6} css={styles.heroSectionContainer}>
          {renderHeroSection()}
        </Grid>
      </Grid>
    );
  };

  const renderMobileContent = () => {
    return (
      <div>
        <div css={styles.heroSectionContainer}>{renderHeroSection()}</div>
        {renderDescription(description)}
        {renderActions()}
      </div>
    );
  };

  return (
    <Dialog open width="large" onClose={() => onDeclinePrePurchaseUpsell(false)} css={styles.dialog} color="blue">
      {isMobile && <Dialog.HeadingGroup>{heading as string}</Dialog.HeadingGroup>}
      <Dialog.Content>
        <TrackedDiv
          withVisibilityTracking={true}
          trackingName="pre_purchase_upsell_modal"
          trackingData={{ modalType: 'pre_purchase_upsell' }}
        >
          {isMobile ? renderMobileContent() : renderDesktopContent()}
        </TrackedDiv>
      </Dialog.Content>
    </Dialog>
  );
};

export default CourseraPlusPrePurchaseUpsellModal;
